import { useCallback, useEffect, useRef, useState } from 'react';
import { useBotContext } from '../store/BotContext';
import { formatCooldown } from '../utils/botSessionLimit';

/** Absolute epoch-ms deadline, persisted so the cooldown survives a reload.
 *  Deliberately NOT keyed by tenant/website (unlike the session/tab keys): the
 *  BE quota is per-IP on the backend, so one deadline per origin is correct. */
const STORAGE_KEY = 'sky360_bot_new_session_cooldown_until';

function readPersisted(): number | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const until = Number(raw);
    // Drop values that are malformed or already elapsed rather than surfacing a
    // cooldown that would immediately tick to zero.
    if (!Number.isFinite(until) || until <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return until;
  } catch {
    return null;
  }
}

/**
 * Live countdown for the per-IP new-session quota.
 *
 * Owns three things the widget needs and nothing else: rehydrating a deadline
 * that outlived the page, ticking it once a second, and clearing it the moment
 * it elapses. Call this ONCE per widget — it runs an interval.
 */
export function useNewSessionCooldown(): {
  /** True while creating a new session would be rejected by the BE. */
  active: boolean;
  secondsLeft: number;
  /** `mm:ss` (or `h:mm:ss`) for display. Empty string when not active. */
  label: string;
  /** Briefly true right after the cooldown elapses, so the widget can say
   *  "you can start a new chat now" instead of the notice just vanishing. */
  justEnded: boolean;
} {
  const { botState, botDispatch } = useBotContext();
  const until = botState.newSessionCooldownUntil;
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [justEnded, setJustEnded] = useState(false);

  // Rehydrate on mount. Runs before the user can interact, so a reload during a
  // cooldown comes back with the input already disabled instead of burning
  // another rejected request to rediscover the limit.
  useEffect(() => {
    const persisted = readPersisted();
    if (persisted) botDispatch({ type: 'SET_NEW_SESSION_COOLDOWN', until: persisted });
  }, [botDispatch]);

  // Mirror state → storage. The write lives here rather than at the throw site
  // so state and storage cannot drift.
  //
  // Skips the first run: on the initial commit `until` is still null because the
  // rehydrate dispatch above has not been applied yet, so clearing here would
  // delete the very deadline we are in the middle of restoring.
  const mirrorReady = useRef(false);
  useEffect(() => {
    if (!mirrorReady.current) {
      mirrorReady.current = true;
      return;
    }
    try {
      if (until && until > Date.now()) localStorage.setItem(STORAGE_KEY, String(until));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private mode / quota — the in-memory cooldown still works */
    }
  }, [until]);

  const tick = useCallback(() => {
    if (!until) {
      setSecondsLeft(0);
      return;
    }
    const remaining = Math.ceil((until - Date.now()) / 1000);
    if (remaining <= 0) {
      setSecondsLeft(0);
      setJustEnded(true);
      botDispatch({ type: 'CLEAR_NEW_SESSION_COOLDOWN' });
      return;
    }
    setSecondsLeft(remaining);
  }, [until, botDispatch]);

  // Retire the "you can start now" nudge on its own so it doesn't sit there for
  // the rest of the visit.
  useEffect(() => {
    if (!justEnded) return;
    const id = window.setTimeout(() => setJustEnded(false), 8000);
    return () => window.clearTimeout(id);
  }, [justEnded]);

  useEffect(() => {
    if (!until) {
      setSecondsLeft(0);
      return;
    }
    tick(); // paint the first value immediately, don't wait 1s for it
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [until, tick]);

  const active = !!until && secondsLeft > 0;
  return {
    active,
    secondsLeft,
    label: active ? formatCooldown(secondsLeft) : '',
    justEnded: justEnded && !active,
  };
}
